// Checkout Forms
import validate from 'checkout-forms/form/validation/validate';
import { getFormInitialValuesFromAddress } from 'checkout-forms/utils/address/address';
import getAddressFieldsConfig from 'checkout-forms/form/configs/address';
import { FIRST_NAME_DATA_STANDARDS, LAST_NAME_DATA_STANDARDS, PHONE_NUMBER } from 'checkout-forms/form/fields';

// Config
import { countryCodeIsInternational } from '../address/addressHelpers';
import makePostcodeOptional from '../address/makePostcodeOptional';
import getInternationalPhoneNumberConfig, { parseInitialPhoneNumber } from '../lazy/internationalPhoneNumberConfig';

export function validateOutOfAreaPostcodes(postcode = '') {
  const outOfAreaPostcodes = ['BF1', 'BF2', 'HA46AP', 'HA46NG', 'HA46EP', 'HA46DQ'];
  const formattedPostcode = postcode.toUpperCase().replace(/ /g, '');
  const addressIsOutOfArea = outOfAreaPostcodes.some((code) => formattedPostcode.startsWith(code));
  if (addressIsOutOfArea) return 'BFPO';

  return undefined;
}

export function getAddressFormConfig(addressFormValues, isDeliveryAddress) {
  const { countryCode } = addressFormValues;

  const ADDRESS_FIELDS = getAddressFieldsConfig({
    international: countryCodeIsInternational(countryCode),
    internationalMode: countryCodeIsInternational(countryCode),
    countryCode,
    addressLines: 3,
    maxAddressLines: 3,
    actions: {},
    configExtras: {},
    allowAsciiOnly: true,
    enableGBCounty: !!addressFormValues?.countyStateOrProvince,
  });

  const postcodeOptional = countryCodeIsInternational(countryCode);
  if (postcodeOptional && !isDeliveryAddress) {
    // international addresses can't be selected for delivery so this should only affect billing addresses
    makePostcodeOptional(ADDRESS_FIELDS);
  }

  const postcodeField = ADDRESS_FIELDS.find((field) => field?.id === 'postcode');

  if (isDeliveryAddress && postcodeField) {
    postcodeField.validationFnc = validateOutOfAreaPostcodes;
  }

  return {
    fields: [
      FIRST_NAME_DATA_STANDARDS,
      LAST_NAME_DATA_STANDARDS,
      getInternationalPhoneNumberConfig(),
      ...ADDRESS_FIELDS,
    ],
  };
}

export function getAddressValidationErrors(addressRecord = {}, isDeliveryAddress) {
  const values = getFormInitialValuesFromAddress(addressRecord);

  let phoneNumberCountryCode;

  if (values.phoneNumber) {
    const parsedPhoneNumber = parseInitialPhoneNumber(values.phoneNumber, values.countryCode || 'GB');
    phoneNumberCountryCode = parsedPhoneNumber.countryCode;
  }

  const addressFormValues = {
    ...values,
    phoneNumberCountryCode,
  };

  const addressFormConfig = getAddressFormConfig(addressFormValues, isDeliveryAddress);

  return validate(addressFormValues, { config: addressFormConfig });
}

export function isNameAndNumberComplete(values) {
  return (
    !!values[FIRST_NAME_DATA_STANDARDS.name] && !!values[LAST_NAME_DATA_STANDARDS.name] && !!values[PHONE_NUMBER.name]
  );
}

function isAddressValid(addressRecord = {}) {
  const errors = getAddressValidationErrors(addressRecord);

  return Object.keys(errors).reduce((acc, key) => {
    if (acc && errors[key]) {
      return false;
    }
    return acc;
  }, true);
}

export default isAddressValid;
